/**
 * PlanLimitsEditor — plan selector + the full restriction sheet of the
 * selected plan. Every row: label · plan default · effective value (editable
 * in edit mode) · OverrideFlag when it differs · per-row reset.
 */

import { ArrowCounterClockwise, Flag, Infinity as InfinityIcon, Check } from "@phosphor-icons/react";
import HoverTip from "../../../../../components/ui/HoverTip";
import { getSystemPlansArray } from "../../../../../services/nivFirebase";
import { UNLIMITED } from "../../../../../services/planLimits";
import { LIMIT_ROWS, formatCurrency, formatLimitValue, planToneClass } from "../clinicDetailConstants";
import OverrideFlag from "./OverrideFlag";

function Switch({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      className={`ncd2-switch${checked ? " is-on" : ""}`}
      onClick={() => onChange(!checked)}
      disabled={disabled}
    >
      <span className="ncd2-switch-knob" />
    </button>
  );
}

function CountInput({ value, onChange, allowUnlimited }) {
  const unlimited = allowUnlimited && Number(value) >= UNLIMITED;
  return (
    <div className="ncd2-count-input">
      <input
        type="number"
        min="0"
        inputMode="numeric"
        dir="ltr"
        value={unlimited ? "" : value ?? ""}
        placeholder={unlimited ? "∞" : "0"}
        disabled={unlimited}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === "" ? 0 : Math.max(0, parseInt(raw, 10) || 0));
        }}
      />
      {allowUnlimited && (
        <HoverTip text={unlimited ? "בטל ״ללא הגבלה״" : "ללא הגבלה"}>
          <button
            type="button"
            className={`ncd2-inf-btn${unlimited ? " is-on" : ""}`}
            onClick={() => onChange(unlimited ? 0 : UNLIMITED)}
            aria-pressed={unlimited}
            aria-label="ללא הגבלה"
          >
            <InfinityIcon size={14} weight="bold" />
          </button>
        </HoverTip>
      )}
    </div>
  );
}

export default function PlanLimitsEditor({ overrides, isEditing, onPlanChange, hidePlanPicker = false }) {
  const { planCode, defaults, draft, diffs, setValue, resetField, resetAll } = overrides;
  const plans = getSystemPlansArray();
  const selected = plans.find((p) => p.code === planCode) || plans[0];
  const diffByKey = Object.fromEntries(diffs.map((d) => [d.key, d]));
  const rows = LIMIT_ROWS.filter((r) => !r.trialOnly || planCode === "trial");

  return (
    <section className="ncd2-card ncd2-plan-card">
      <header className="ncd2-card-head">
        <span className={`ncd2-plan-tile ${planToneClass(planCode)}`}>
          <Flag size={15} weight="fill" />
        </span>
        <div className="ncd2-card-titles">
          <span className="ncd2-card-eyebrow">מגבלות</span>
          <h3 className="ncd2-card-title">
            {selected.nameHebrew}
            {selected.monthlyPrice > 0 && (
              <span className="ncd2-muted ncd2-plan-price">{formatCurrency(selected.monthlyPrice)}/חודש</span>
            )}
            <OverrideFlag diffs={diffs} label={`${diffs.length} חריגות`} />
          </h3>
        </div>
        {isEditing && diffs.length > 0 && (
          <button type="button" className="ncd2-btn ncd2-btn-ghost ncd2-btn-sm" onClick={resetAll}>
            <ArrowCounterClockwise size={14} /> אפס הכל לברירת המחדל
          </button>
        )}
      </header>

      {isEditing && !hidePlanPicker && (
        <div className="ncd2-plan-picker" role="radiogroup" aria-label="בחירת מסלול">
          {plans.map((p) => (
            <button
              key={p.code}
              type="button"
              role="radio"
              aria-checked={p.code === planCode}
              className={`ncd2-plan-chip ${planToneClass(p.code)}${p.code === planCode ? " is-active" : ""}`}
              onClick={() => p.code !== planCode && onPlanChange(p.code)}
            >
              {p.code === planCode && <Check size={12} weight="bold" />}
              {p.nameHebrew}
            </button>
          ))}
        </div>
      )}

      <div className="ncd2-limits">
        <div className="ncd2-limits-head" aria-hidden="true">
          <span>מגבלה</span>
          <span>ברירת מחדל</span>
          <span>בפועל</span>
          <span />
        </div>
        {rows.map((row) => {
          const diff = diffByKey[row.key];
          const value = row.type === "readonly" ? defaults[row.key] : draft[row.key];
          const editable = isEditing && row.type !== "readonly";
          return (
            <div key={row.key} className={`ncd2-limit-row${diff ? " is-override" : ""}`}>
              <div className="ncd2-limit-label">
                <span>{row.label}</span>
                {row.hint && <span className="ncd2-limit-hint">{row.hint}</span>}
              </div>
              <span className="ncd2-limit-default ncd2-muted">{formatLimitValue(row, defaults[row.key])}</span>
              <div className="ncd2-limit-value" data-default={formatLimitValue(row, defaults[row.key])}>
                {editable && row.type === "bool" && (
                  <Switch checked={!!value} onChange={(v) => setValue(row.key, v)} label={row.label} />
                )}
                {editable && (row.type === "count" || row.type === "int") && (
                  <CountInput value={value} onChange={(v) => setValue(row.key, v)} allowUnlimited={row.type === "count"} />
                )}
                {!editable && (
                  <strong className={diff ? "ncd2-override-value" : ""}>{formatLimitValue(row, value)}</strong>
                )}
                {diff && <OverrideFlag diffs={[diff]} />}
              </div>
              <div className="ncd2-limit-actions">
                {editable && diff && (
                  <HoverTip text="חזרה לברירת המחדל של המסלול">
                    <button
                      type="button"
                      className="ncd2-icon-btn"
                      onClick={() => resetField(row.key)}
                      aria-label={`אפס ${row.label}`}
                    >
                      <ArrowCounterClockwise size={14} />
                    </button>
                  </HoverTip>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!hidePlanPicker && selected.featuresListHebrew?.length > 0 && (
        <ul className="ncd2-plan-features">
          {selected.featuresListHebrew.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
